/**
 * 通貨フォーマット用ユーティリティ
 *
 * アプリケーション内で統一された通貨表示を提供します。
 */

export interface CurrencyFormatOptions {
  /** 通貨コード (デフォルト: 'JPY') */
  currency?: string;
  /** 小数点以下の最小桁数 (デフォルト: 0) */
  minimumFractionDigits?: number;
  /** 小数点以下の最大桁数 (デフォルト: 2) */
  maximumFractionDigits?: number;
}

/**
 * 金額を通貨記号付きでフォーマット
 * UI表示用（¥1,234,567）
 */
export const formatCurrency = (amount: number, options: CurrencyFormatOptions = {}): string => {
  const { currency = 'JPY', minimumFractionDigits = 0, maximumFractionDigits = 2 } = options;

  return new Intl.NumberFormat('ja-JP', {
    style: 'currency',
    currency,
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(amount);
};

/**
 * 金額を数値形式でフォーマット
 * CSV出力やテキスト表示用（1,234,567）
 */
export const formatCurrencyNumber = (amount: number): string => {
  return amount.toLocaleString('ja-JP');
};

/**
 * 陸送費専用フォーマット
 * 小数点なしの円表示（¥1,234,567）
 */
export const formatTransportFee = (amount: number): string => {
  return formatCurrency(amount, { minimumFractionDigits: 0 });
};

/**
 * null や undefined を考慮した安全な通貨フォーマット
 */
export const formatCurrencySafe = (
  amount: number | null | undefined,
  options: CurrencyFormatOptions = {},
  fallback: string = '-'
): string => {
  if (amount == null) return fallback;
  return formatCurrency(amount, options);
};

/**
 * CSV出力用の安全な通貨フォーマット
 */
export const formatCurrencyForCSV = (amount: number | null | undefined): string => {
  if (amount == null) return '';
  return formatCurrencyNumber(amount);
};
